import { PriceUI } from "./PriceUI";
import { StandardProps } from "@/components/componentMap";
import { useVariant } from "@/hooks/useVariant/useVariant";
import type {
  Display,
  Price,
  PriceRange,
  ReductionHistory,
  ReductionHistoryRange,
} from "@/types/prodcatBaseTypes";
import { isPerMetre } from "@/utils/product/isPerMetre";
import {
  isM2MRollerBlind,
  isM2MRomanBlindOrCurtain,
} from "@/utils/product/isMadeToMeasure";
import { isAvailableToOrder } from "@/utils/product/isAvailableToOrder";
import { getColourGroupForVariant } from "@/utils/product/getColourGroupForVariant";
import { calculateMinRollerBlindPrice } from "../MadeToMeasureForm/util/calculateMinRollerBlindPrice";

type VariantGroupPriceRange = {
  __typename?: "PriceRange";
  display: Display;
  reductionHistory: Array<ReductionHistoryRange>;
};

type M2mRollerBlindPriceData = {
  was?: string | null;
  then1?: string | null;
  then2?: string | null;
  now?: string | null;
};

function getPriceValueOrRange(
  priceData: { max: string; min: string } | string | undefined,
) {
  if (typeof priceData === "object") {
    return priceData.max === priceData.min
      ? priceData.min
      : {
          from: priceData.min,
          to: priceData.max,
        };
  }
  return priceData || "";
}

function getReductionHistoryItem(
  reductionHistory: Array<ReductionHistoryRange | ReductionHistory>,
  chronology: number,
) {
  const historyItem = reductionHistory?.find(
    (item) => item.chronology === chronology,
  );
  return getPriceValueOrRange(historyItem?.display);
}

function convertVariantPriceRangeToFormattedPrice(
  variantPriceRange: PriceRange | Price | VariantGroupPriceRange | undefined,
) {
  return variantPriceRange
    ? {
        was: getReductionHistoryItem(variantPriceRange.reductionHistory, 0),
        then1: getReductionHistoryItem(variantPriceRange.reductionHistory, 1),
        then2: getReductionHistoryItem(variantPriceRange.reductionHistory, 2),
        now: getPriceValueOrRange(variantPriceRange.display),
      }
    : undefined;
}

function calculateM2mRollerBlindPrice(
  formattedPriceData: M2mRollerBlindPriceData | undefined,
) {
  return formattedPriceData
    ? {
        was: calculateMinRollerBlindPrice(formattedPriceData.was),
        then1: calculateMinRollerBlindPrice(formattedPriceData.then1),
        then2: calculateMinRollerBlindPrice(formattedPriceData.then2),
        now: calculateMinRollerBlindPrice(formattedPriceData.now),
      }
    : undefined;
}

export function Price({
  product,
  configProps,
  flags,
}: Pick<StandardProps, "product" | "configProps" | "flags">) {
  const { variantPriceRange } = product;
  const { variant, isSelected } = useVariant(product);

  const isRollerBlindPrice =
    isM2MRollerBlind(product) && isAvailableToOrder(product);
  const variantGroupsDefault = Boolean(configProps?.useVariantGroups ?? true);
  const isResponsiveStylesEnabled =
    flags.isResponsiveStylesEnabled.value === true;

  let priceRange: PriceRange | Price | VariantGroupPriceRange | undefined;

  if (variantGroupsDefault && !isSelected) {
    const variantGroup = getColourGroupForVariant(
      product.variantGroups,
      variant.id,
    );
    const variantGroupPriceRange = variantGroup?.variantPriceRange;

    priceRange = variantGroupPriceRange || variantPriceRange;
  } else {
    priceRange = isSelected ? variant.price : variantPriceRange;
  }

  const brand = product.brand?.name || "";
  let formattedPrice = convertVariantPriceRangeToFormattedPrice(priceRange);

  if (isRollerBlindPrice) {
    formattedPrice = calculateM2mRollerBlindPrice(
      formattedPrice as M2mRollerBlindPriceData,
    );
  }

  const priceSuffix =
    isPerMetre(product) || isM2MRomanBlindOrCurtain(product)
      ? "per metre"
      : undefined;

  const pricePrefix = isRollerBlindPrice ? "Blind price from" : undefined;

  return (
    <PriceUI
      price={formattedPrice}
      pricePrefix={pricePrefix}
      priceSuffix={priceSuffix}
      brand={brand}
      isResponsiveStylesEnabled={isResponsiveStylesEnabled}
    />
  );
}
